import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import minMax from 'dayjs/plugin/minMax';
import timezone from 'dayjs/plugin/timezone';
import { md5 } from 'js-md5';
import { groupBy, reduce, round, sortBy, uniqBy } from 'lodash-es';
import { computed, reactive, ref } from 'vue';
import useAbortController from '~/common/composables/abort-controller.js';
import { useCommonImports } from '~/common/composables/common-imports.composable.js';
import { csvInjectionProtector } from '~/common/utils/common.utils.js';
import { useTerraStore } from '~/terra/store/terra.store.js';
import { useChartsConstants } from '~/terra/utils/charts-constants';

dayjs.extend(minMax);
dayjs.extend(isSameOrBefore);
dayjs.extend(timezone);

export function useExportTerraReport() {
  const terra_store = useTerraStore();
  const controllers = useAbortController();

  const { auth_store, common_store, $t, $services } = useCommonImports();
  const { sheet_column_specific_keys, sheet_column_specific_titles, sheet_column_titles, sheet_status_map } = useChartsConstants();
  if (auth_store?.logged_in_user_details?.timezone)
    dayjs.tz.setDefault(auth_store?.logged_in_user_details?.timezone);

  const is_report_exporting = ref(false);
  const is_split_enabled = ref(false);
  const filters = ref(null);

  const state = reactive({
    progress_data: {},
    classes_breakdown: [],
  });
  const container_name = computed(() => terra_store.container?.name);

  const columns_titles = computed(() => {
    const clones_titles = auth_store.check_split('grs_export_summary') && is_split_enabled.value ? [...sheet_column_specific_titles] : [...sheet_column_titles];
    if (auth_store.check_split('terra_cpr_id_column'))
      clones_titles.splice(1, 0, 'ID');
    return clones_titles;
  });

  const sheet_name = computed(() => container_name.value || 'terra-report');
  const project_group_map = computed(() => {
    const project_group_names = {};
    Object.values(terra_store.container?.groups || {}).forEach((group) => {
      Object.values(group.projects || {}).forEach((project) => {
        project_group_names[project.uid] = `${group.name}Ⳇ${project.name}`;
      });
    });
    return project_group_names;
  });

  function getWorkflowProjectMap(reports = []) {
    const workflow_project_map = {};
    const grouped_workflow = groupBy(sortBy(reports, ['workflow']), 'workflow_uid');
    for (const [workflow, project] of Object.entries(grouped_workflow)) {
      const filtered_projects = project.filter(project => project.project);
      workflow_project_map[workflow] = groupBy(sortBy(filtered_projects, ['project']), 'project_uid');
    }
    return workflow_project_map;
  }

  const fixed_columns = computed(() => {
    if (is_split_enabled.value && auth_store.check_split('grs_export_summary'))
      return sheet_column_specific_keys;
    return [
      { key: 'number' },
      ...(auth_store.check_split('terra_cpr_id_column') ? [{ key: 'id', width: 20 }] : []),
      { key: 'activity', width: 20 },
      { key: 'workflow', width: 20 },
      { key: 'layer', width: 20 },
      { key: 'sublayer', width: 20 },
      { key: 'uom' },
      { key: 'scope' },
      { key: 'weight' },
      { key: 'completed' },
      { key: 'remaining' },
      { key: 'progress' },
      { key: 'status', width: 12 },
      { key: 'planned_start', width: 15, style: { numFmt: 'DD/MM/YYYY' } },
      { key: 'planned_finish', width: 15, style: { numFmt: 'DD/MM/YYYY' } },
      { key: 'expected_work_rate', width: 15 },
      { key: 'actual_start', width: 15, style: { numFmt: 'DD/MM/YYYY' } },
      { key: 'actual_finish', width: 15, style: { numFmt: 'DD/MM/YYYY' } },
      { key: 'est_finish_date', width: 15, style: { numFmt: 'DD/MM/YYYY' } },
    ];
  });

  function utcToDate(date) {
    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
  }

  async function fetchProgressData(report_data) {
    try {
      const project_uid = uniqBy(report_data, 'project_uid').map(project => project.project_uid);
      state.uid_name_map = report_data.reduce((acc, item) => {
        acc[item.workflow_uid] = item.workflow;
        acc[item.project_uid] = item.project;
        acc[item.field_uid] = item.field;
        return acc;
      }, {});
      if (!project_uid.length)
        return {};
      const { data } = await $services.terra.post({
        attribute: 'progress-data',
        body: {
          export: true,
          project: project_uid,
          group_by: 'project',
        },
      });
      const filtered_data = getFilteredData(data, 'progress_data');
      return reduce(filtered_data, (result, item) => {
        const workflow_uid = item.workflow.uid;
        const workflow_name = item.workflow.name;
        const project_uid = item.project.uid;
        const project_name = item.project.name;
        const field_uid = item.field.uid;
        const field_name = item.field.name;
        const date = dayjs(item.date).format('DD-MMM-YY');

        const layer = csvInjectionProtector(project_group_map.value[item.project.uid]) || '';
        const split_layer = layer?.split('Ⳇ');

        result[workflow_uid] = result[workflow_uid] || {};
        result[workflow_uid][project_uid] = result[workflow_uid][project_uid] || {};
        result[workflow_uid][project_uid][field_uid] = result[workflow_uid][project_uid][field_uid] || {};
        result[workflow_uid][project_uid][field_uid][date] = item.value;

        const data = {
          workflow: workflow_name,
          activity: field_name,
          project: project_name,
          layer: (split_layer?.[0] || ''),
          sublayer: split_layer[1] || '',
          date: formattedDate(date),
          actual_date: date,
          value: item.value,
        };
        if (!result.custom_agent_data)
          result.custom_agent_data = [];
        result.custom_agent_data.push(data);
        return result;
      }, {});
    }
    catch (er) {
      logger.error(er);
      return {};
    }
  }

  function setupHeaderInfo(worksheet) {
    worksheet.mergeCells('B2:L2');
    worksheet.getCell('B2').value = $t('Construction progress report');

    const second_rows = worksheet.getRow(2);
    second_rows.height = 42;
    worksheet.getCell('B2').style = {
      font: {
        size: 30,
        bold: true,
        color: { argb: '0b5394' },
      },
      alignment: {
        vertical: 'middle',
        wrapText: true,
      },
    };
    second_rows.eachCell((cell) => {
      cell.border = {
        bottom: { style: 'thin', color: { argb: '0b5394' } },
      };
    });
    const header_cells = {
      B4: {
        merge_with: 'C4',
        value: 'ORGANIZATION',
        style: true,
      },
      D4: {
        merge_with: 'F4',
        value: csvInjectionProtector(auth_store.current_organization?.name) || '',
        style: false,
      },
      G4: {
        merge_with: 'I4',
        value: 'ASSET',
        style: true,
      },
      J4: {
        merge_with: 'L4',
        value: csvInjectionProtector(common_store?.active_asset?.name) || '',
        style: false,
      },
      B5: {
        merge_with: 'C5',
        value: 'MAP',
        style: true,
      },
      D5: {
        merge_with: 'F5',
        value: csvInjectionProtector(container_name.value) || '',
        style: false,
      },
      G5: {
        merge_with: 'I5',
        value: 'DATE',
        style: true,
      },
      J5: {
        merge_with: 'L5',
        value: dayjs().format('MMM D, YYYY'),
        style: false,
      },
    };

    for (const [cell, property] of Object.entries(header_cells)) {
      worksheet.mergeCells(`${cell}:${property.merge_with}`);
      const first_cell = worksheet.getCell(cell);
      const cell_border = {
        bottom: { style: 'thin', color: { argb: 'd9d9d9' } },
      };
      const second_cell = worksheet.getCell(property.merge_with);
      second_cell.border = cell_border;
      first_cell.value = property.value;
      if (property.style) {
        first_cell.style = {
          font: {
            bold: true,
            color: { argb: '666666' },
          },
          border: cell_border,
        };
      }
    }
  }

  function setupRowHeader(worksheet, titles, dates = []) {
    let row_starting = 1;
    if (!is_split_enabled.value || auth_store.check_split('grs_export_summary_header_rows'))
      row_starting = 8;

    const starting_row = worksheet.getRow(row_starting); // Add data from 8th row
    starting_row.height = 28;
    starting_row.values = [...titles, ...(dates.map(date => dayjs(date).startOf('day').toDate()))];
    starting_row.eachCell((cell, colNumber) => {
      cell.style = {
        font: {
          size: 9,
          color: { argb: '000000' },
          bold: true,
        },
        alignment: {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
        },
      };
      if (colNumber <= titles.length) {
        cell.style.fill = createCellFill({ color: 'efefef' });
      }
      else {
        cell.value = utcToDate(cell.value);
        cell.style.fill = createCellFill({ color: 'bdd7ee' });
      }
    });
  }

  function formattedDate(date) {
    let formatted_date = null;
    if (!date) {
      formatted_date = '';
    }
    else {
      const date_obj = dayjs(date).startOf('day');
      if (date_obj.isValid())
        formatted_date = utcToDate(date_obj.toDate());
      else
        formatted_date = '';
    }
    return formatted_date;
  }

  function getDatesBetween(min_date, max_date) {
    const dates = [];
    let current_date = min_date || dayjs(min_date);
    const end_date = max_date || dayjs(max_date);
    while (current_date.isSameOrBefore(end_date)) {
      dates.push(current_date.format('DD-MMM-YY'));
      current_date = current_date.add(1, 'day');
    }
    return dates;
  }

  function getMinMaxDate(projects = []) {
    if (filters.value?.export_type === 'custom' && filters.value?.date_range) {
      return {
        min_date: dayjs(filters.value.start_date),
        max_date: dayjs(filters.value.end_date),
      };
    }
    const flatten_dates = projects.flatMap(data => [data.actual_start, data.actual_finish || dayjs().toISOString(), data.planned_start, data.planned_finish]);
    const filtered_dates = reduce(flatten_dates, (result, date) => {
      if (date) {
        const formatted_date = dayjs(date).format('DD-MMM-YY');
        result.push(dayjs(formatted_date));
      }
      return result;
    }, []);
    return {
      min_date: dayjs.min(filtered_dates),
      max_date: dayjs.max(filtered_dates),
    };
  }

  function createCellBorder(options) {
    const { borders, style, color } = options;
    const cell_borders = {};
    borders.forEach((side) => {
      cell_borders[side] = { style, color: { argb: color } };
    });
    return cell_borders;
  }

  function createCellFill(options = {}) {
    return {
      type: options?.type || 'pattern',
      pattern: options?.pattern || 'solid',
      fgColor: { argb: options?.color || 'FFFFFF' },
    };
  }

  function expectedDateColor(cell, field) {
    const current_value = cell.value;
    let cell_bg_color = null;
    if (!current_value || !field.planned_start || !field.planned_finish) {
      cell.style.fill = createCellFill({ color: 'FFFFFF' });
      return;
    }
    const current_date = cell._column._key;
    const planned_start = dayjs(field.planned_start)?.format('DD-MMM-YY');
    const planned_finish = dayjs(field.planned_finish)?.format('DD-MMM-YY');
    const is_before_start = dayjs(current_date).isBefore(planned_start);
    const is_between_start_and_finish = dayjs(current_date).isBetween(planned_start, planned_finish, null, '[]');
    const is_after_finish = dayjs(current_date).isAfter(planned_finish);

    if (is_before_start) {
      cell_bg_color = cell.value > 0 ? 'd9ead3' : 'FFFFFF'; // green
    }
    else if (is_between_start_and_finish) {
      const expected_rate = field.planned_work_rate;
      if (current_value < expected_rate)
        cell_bg_color = 'f4cccc'; // red
      else if (current_value === expected_rate)
        cell_bg_color = 'FFFFFF';
      else if (current_value > expected_rate)
        cell_bg_color = 'd9ead3'; // green
    }
    else if (is_after_finish) {
      cell_bg_color = cell.value > 0 ? 'f4cccc' : 'FFFFFF'; // red
    }

    cell.style.fill = createCellFill({ color: cell_bg_color || 'FFFFFF' });
  }

  function getUniqSheetName(sheet_names, current_sheet) {
    const sheet_name = current_sheet.replace(/[*?:/\\[\]]/g, ' ')?.substring(0, 29);
    if (sheet_names.includes(sheet_name)) {
      const occurrence = sheet_names.filter(element => element?.substring(0, 29) === sheet_name).length;
      return `${sheet_name}-${occurrence + 1}`;
    }
    return sheet_name;
  }

  function cancelExportingTerraReport() {
    controllers.abort('export_terra_report');
    setExporting(false);
  }

  function getValuesForSpecificDate(workflow, project, activity, between_dates = []) {
    return between_dates.reduce((result, date) => {
      const date_obj = state.progress_data?.[workflow]?.[project]?.[activity] || {};
      result[date] = date_obj?.[date] || '';
      return result;
    }, {});
  }

  function setExporting(value, split = false, applied_filters = null) {
    is_report_exporting.value = value;
    is_split_enabled.value = split;
    filters.value = applied_filters;
  }

  function getFieldValues(field) {
    const { workflow_uid, field_uid, project_uid } = field;
    const layer = csvInjectionProtector(project_group_map.value[field.project_uid]) || '';
    const split_layer = layer?.split('Ⳇ');
    const id = md5(workflow_uid + project_uid + field_uid);

    return {
      activity: csvInjectionProtector(field.field) || '',
      activity_uid: field_uid,
      id: auth_store.check_split('grs_export_summary') && is_split_enabled.value ? id.substring(0, 10) : id,
      workflow: csvInjectionProtector(field?.workflow) || '',
      workflow_uid,
      layer: (split_layer?.[0] || ''),
      sublayer_uid: project_uid,
      sublayer: split_layer[1] || '',
      uom: csvInjectionProtector(field.units),
      scope: field?.planned_work || 0,
      weight: Number.parseFloat(((field?.weightage || 0) * 100).toFixed(2)),
      completed: field?.actual_work || 0,
      remaining: field?.remaining_work || '',
      progress: round(field?.percent_complete * 100),
      status: field?.status === 'NA' ? '' : (field?.status || ''),
      planned_start: formattedDate(field?.planned_start),
      planned_finish: formattedDate(field?.planned_finish),
      expected_work_rate: field?.planned_work_rate,
      actual_start: formattedDate(field?.actual_start),
      actual_finish: formattedDate(field?.actual_finish),
      est_finish_date: formattedDate(field?.est_finish),
    };
  }

  function sheetEachRow(row, field) {
    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.name = fixed_columns.value[colNumber - 1]?.key;
      cell.style = {
        font: { color: { argb: '434343' } },
        alignment: { horizontal: ['activity', 'number'].includes(cell.name) ? 'left' : 'center' },
      };
      if (colNumber <= fixed_columns.value.length) {
        cell.style.border = createCellBorder({ borders: ['bottom'], style: 'thin', color: 'b7b7b7' });
      }
      else {
        cell.style.border = createCellBorder({ borders: ['top', 'bottom', 'left', 'right'], style: 'dotted', color: 'b7b7b7' });
        expectedDateColor(cell, field);
      }
      if (cell.name === 'status') {
        const status_name = cell?.value?.toLocaleLowerCase();
        cell.style.fill = createCellFill({ color: sheet_status_map[status_name] });
      }
    });
  }

  function setSheetBasicInfo(worksheet, projects) {
    worksheet.properties.outlineProperties = { summaryBelow: false }; // collapse button on top
    const flat_project = Object.values(projects).flatMap(project => project);
    const { min_date, max_date } = getMinMaxDate(flat_project);
    let dates_between = [];
    let date_columns = [];

    if (!is_split_enabled.value || auth_store.check_split('grs_export_summary_datewise_progress')) {
      dates_between = getDatesBetween(min_date, max_date);
      date_columns = dates_between?.map(date => ({ key: date }));
    }

    worksheet.columns = [...fixed_columns.value, ...date_columns];

    if (!is_split_enabled.value || auth_store.check_split('grs_export_summary_header_rows'))
      setupHeaderInfo(worksheet);

    setupRowHeader(worksheet, columns_titles.value, dates_between);
    return { dates_between };
  }

  function calculateProgress(fields) {
    let total_weighted_actual = 0;
    let total_weighted_planned = 0;

    fields.forEach((field) => {
      const weightage = field?.weightage || 0;
      const actual_work = field?.actual_work || 0;
      const planned_work = field?.planned_work || 0;

      total_weighted_actual += actual_work * weightage;
      total_weighted_planned += planned_work * weightage;
    });

    if (total_weighted_planned === 0)
      return 0;
    return round((total_weighted_actual / total_weighted_planned) * 100);
  }

  function setWorkflowRow(worksheet, workflow, dates_between, index = 1, projects = {}) {
    const empty_cols = Array.from({ length: [...columns_titles.value, ...dates_between].length - 2 }, () => '');

    if (!is_split_enabled.value || auth_store.check_split('grs_export_summary_header_rows')) {
      const all_fields = Object.values(projects).flat();
      const workflow_progress = calculateProgress(all_fields);

      const workflow_row_data = [index, workflow, ...empty_cols];

      const progress_col_index = fixed_columns.value.findIndex(col => col.key === 'progress');
      if (progress_col_index !== -1) {
        workflow_row_data[progress_col_index] = workflow_progress;
      }

      const workflow_row = worksheet.addRow(workflow_row_data);

      // Mark this row as a workflow row for later exclusion from conditional formatting
      workflow_row._isWorkflowRow = true;

      workflow_row.eachCell({ includeEmpty: true }, (cell) => {
        cell.style = {
          font: { color: { argb: '000000' }, size: 11, bold: true },
          alignment: { horizontal: 'center' },
          fill: createCellFill({ color: 'F7DCB9' }),
        };
      });
    }
    return { empty_cols };
  }

  function setProjectRow(worksheet, row_data, fields = []) {
    const project_progress = calculateProgress(fields);
    const project_row_data = [...row_data];

    const progress_col_index = fixed_columns.value.findIndex(col => col.key === 'progress');
    if (progress_col_index !== -1) {
      project_row_data[progress_col_index] = project_progress;
    }

    const project_row = worksheet.addRow(project_row_data);

    // Mark this row as a project row for later exclusion from conditional formatting
    project_row._isProjectRow = true;

    // Always apply gray background to project rows (including empty cells)
    project_row.eachCell({ includeEmpty: true }, (cell) => {
      cell.style = {
        font: {
          color: { argb: '000000' },
          size: 11,
          bold: true,
        },
        alignment: { horizontal: 'center' },
        fill: createCellFill({ color: 'd9d9d9' }),
      };
    });

    return project_row;
  }

  function addSummarySheet(workbook, group_workflow_project, report_data) {
    const freezing_column = is_split_enabled.value ? auth_store.check_split('grs_export_summary_datewise_progress') : true;
    const worksheet = workbook.addWorksheet('Summary', {
      views: [{
        state: 'frozen',
        xSplit: freezing_column ? fixed_columns.value.length : 0,
        showGridLines: false,
      }],
    });
    const { dates_between } = setSheetBasicInfo(worksheet, report_data);
    Object.entries(group_workflow_project).forEach(([workflow_uid, projects], w_idx) => {
      const workflow = state.uid_name_map[workflow_uid];
      const { empty_cols } = setWorkflowRow(worksheet, workflow, dates_between, w_idx + 1, projects);
      setFieldsData(worksheet, projects, workflow_uid, dates_between, empty_cols, w_idx + 1);
    });
  }

  function setFieldsData(worksheet, projects, workflow, dates_between, empty_cols, s_no = null) {
    Object.entries(projects).forEach(([project_uid, fields], index) => {
      const project_name = state.uid_name_map[project_uid];
      const level_one = s_no ? `${s_no}.${index + 1}` : `1.${index + 1}`;
      const header_row_data = [level_one, csvInjectionProtector(project_name), ...empty_cols];
      const fields_asc = sortBy(fields, ['field_index']);
      const project_row = setProjectRow(worksheet, header_row_data, fields_asc);
      fields_asc.forEach((field, indx) => {
        const dates_fields = getValuesForSpecificDate(workflow, project_uid, field.field_uid, dates_between);
        const row_values = {
          number: `${level_one}.${indx + 1}`,
          ...getFieldValues(field),
          ...(dates_fields),
        };
        const row = worksheet.addRow(row_values);

        row.outlineLevel = s_no ? 2 : 1;
        if (is_split_enabled.value && !auth_store.check_split('grs_export_summary_group_rows'))
          row.outlineLevel = 0;

        sheetEachRow(row, field);
        row.commit();
      });

      if (s_no) {
        project_row.outlineLevel = 1;
        if (is_split_enabled.value && !auth_store.check_split('grs_export_summary_group_rows'))
          project_row.outlineLevel = 0;
      }
    });
  }

  async function getFormattedData(report_data) {
    const group_workflow_project = getWorkflowProjectMap(report_data);
    const progress_data = await fetchProgressData(report_data);
    await getClassBreakDownData();
    return { group_workflow_project, progress_data, classes_breakdown: state.classes_breakdown };
  }

  async function getClassBreakDownData() {
    let projects = filters.value?.projects || [];
    if (!filters.value || filters.value?.export_type === 'all')
      projects = [];
    const { data } = await $services.terra.post({
      attribute: `container-views/${terra_store.container?.uid}/classes-breakdown`,
      body: {
        projects,
      },
    });
    state.classes_breakdown = data?.data || [];
  }

  async function addClassBreakDownSheet(workbook) {
    const worksheet = workbook.addWorksheet('Classes');

    const header_name_map = {
      layer: 'Layer',
      sublayer: 'Sublayer',
      class_group: 'Class group',
      class: 'Class',
      feature_count: 'Count',
    };
    const headers = Object.keys(header_name_map).map(key => ({ value: key, header: header_name_map[key] || key, width: 25 }));
    worksheet.columns = headers;
    // Apply styles to header cells
    worksheet.getRow(1).height = 25;
    worksheet.getRow(1).eachCell((cell) => {
      cell.style = {
        font: {
          size: 9,
          color: { argb: '000000' },
          bold: true,
        },
        alignment: {
          vertical: 'middle',
          horizontal: 'center',
          wrapText: true,
          indent: 1,
        },
        fill: createCellFill({ color: 'efefef' }),
      };
    });

    const rows = (state.classes_breakdown || []).map((value) => {
      return headers.map(header => value[header.value]);
    });
    worksheet.addRows(rows);

    // Apply styles to rows
    worksheet.eachRow((row, rowNumber) => {
      if (rowNumber > 1) { // Skip the header row (row 1)
        row.eachCell((cell) => {
          cell.style = {
            font: {
              size: 11,
              color: { argb: '000000' },
            },
            alignment: {
              vertical: 'middle',
              horizontal: 'left',
              wrapText: true,
            },
          };
        });
      }
    });
  }

  function getFilteredData(data, type = 'report') {
    if (!filters.value || filters.value?.export_type === 'all')
      return data;
    return data.filter((data) => {
      let is_filtered = true;

      if (filters.value.projects?.length) {
        is_filtered = filters.value.projects.includes(data.project_uid || data?.project?.uid);
      }

      if (is_filtered && filters.value.fields?.length) {
        is_filtered = filters.value.fields.includes(data.field_uid || data?.field?.uid);
      }

      if (is_filtered && filters.value?.date_range && type !== 'report') {
        const start_date = filters.value.start_date;
        const end_date = filters.value.end_date;
        const date = data.date;
        is_filtered = dayjs(date).isBetween(dayjs(start_date), dayjs(end_date), 'day', '[]'); // Inclusive range
      }

      return is_filtered;
    });
  }

  async function exportTerraReports(report_data) {
    controllers.add('export_terra_report');
    const ExcelJS = await import('exceljs');
    const { saveAs } = await import('file-saver');
    const updated_report_data = getFilteredData(report_data);
    const { group_workflow_project, progress_data } = await getFormattedData(updated_report_data);
    state.progress_data = progress_data;
    const workbook = new ExcelJS.Workbook();
    try {
      addSummarySheet(workbook, group_workflow_project, report_data);

      if (!is_split_enabled.value) {
        Object.entries(group_workflow_project).forEach(([workflow_uid, projects]) => {
          const workflow = state.uid_name_map[workflow_uid];
          const worksheet_names = workbook.worksheets.map(sheet => sheet.name);
          const uniq_sheet_name = getUniqSheetName(worksheet_names, workflow);
          const worksheet = workbook.addWorksheet(csvInjectionProtector(uniq_sheet_name), {
            views: [{ state: 'frozen', xSplit: fixed_columns.value.length, showGridLines: false }],
          });
          const { dates_between } = setSheetBasicInfo(worksheet, projects);
          const { empty_cols } = setWorkflowRow(worksheet, workflow, dates_between, 1, projects);
          setFieldsData(worksheet, projects, workflow_uid, dates_between, empty_cols);
        });
      }

      const color_scale_rule = [{
        type: 'colorScale',
        priority: 0,
        cfvo: [
          { type: 'num', value: 0 },
          { type: 'num', value: 100 },
        ],
        color: [{ argb: 'FFFFFF' }, { argb: '57BB84' }],
      }];
      workbook.eachSheet((worksheet) => {
        const columns = worksheet._columns;
        columns.forEach((column) => {
          if (column._key === 'progress') {
            // Build ranges excluding workflow and project rows
            const ranges = [];
            let rangeStart = null;

            worksheet.eachRow({ includeEmpty: false }, (row, rowNumber) => {
              if (rowNumber < 10)
                return; // Skip header rows

              // If this is a workflow or project row, close the current range
              if (row._isWorkflowRow || row._isProjectRow) {
                if (rangeStart !== null) {
                  ranges.push(`${column.letter}${rangeStart}:${column.letter}${rowNumber - 1}`);
                  rangeStart = null;
                }
              }
              else {
                if (rangeStart === null) {
                  rangeStart = rowNumber;
                }
              }
            });

            // Close the last range if it exists
            if (rangeStart !== null) {
              ranges.push(`${column.letter}${rangeStart}:${column.letter}${worksheet.rowCount}`);
            }

            // Apply conditional formatting to each range
            ranges.forEach((range) => {
              worksheet.addConditionalFormatting({
                ref: range,
                rules: color_scale_rule,
              });
            });
          }
        });
      });

      await addClassBreakDownSheet(workbook);
      if (is_report_exporting.value) {
        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${sheet_name.value}.xlsx`);
        setTimeout(() => {
          setExporting(false);
        }, 3000);
      }
    }
    catch (err) {
      logger.error(err);
      setExporting(false);
    }
  }
  async function progressFiles(report_data) {
    const Papa = await import('papaparse');

    const conditional_naavix_and_terra_charts_bi_names = [
      { key: 'workflow', header: 'Activity', width: 20 },
      { key: 'activity', header: 'Subactivity', width: 20 },
      { key: 'layer', header: 'Layer', width: 20 },
      { key: 'sublayer', header: 'Sublayer', width: 20 },
    ];
    const progress_data_columns = [
      { key: 'id', header: 'ID' },
      ...conditional_naavix_and_terra_charts_bi_names,
      { key: 'uom', header: 'UOM' },
      { key: 'scope', header: 'Scope' },
      { key: 'weight', header: 'Weight' },
      { key: 'completed', header: 'Completed', width: 20 },
      { key: 'actual_start', header: 'Actual Start', width: 15 },
      { key: 'actual_finish', header: 'Actual Finish', width: 15 },
      { key: 'remaining', header: 'Remaining' },
      { key: 'progress', header: '% Progress' },
      { key: 'status', width: 12, header: 'Status' },
      { key: 'planned_start', header: 'Planned Start', width: 15 },
      { key: 'planned_finish', header: 'Planned Finish', width: 15 },
      { key: 'expected_work_rate', header: 'Expected Work rate', width: 15 },
      { key: 'est_finish_date', header: 'Est. Finish date', width: 15 },
    ];

    const progress_history_columns = [
      { key: 'id', header: 'ID' },
      ...conditional_naavix_and_terra_charts_bi_names,
      { key: 'date', header: 'Date', width: 15 },
      { key: 'scope', header: 'Scope', width: 15 },
      { key: 'value', header: 'Work done' },
    ];

    const classes_breakdown_columns = [
      { key: 'layer', header: 'Layer' },
      { key: 'sublayer', header: 'Sublayer' },
      { key: 'class_group', header: 'Class group' },
      { key: 'class', header: 'Class' },
      { key: 'feature_count', header: 'Count' },
    ];

    function getProgressDataFile(group_workflow_project) {
      const { rows } = workflowsData(group_workflow_project);
      const csv = Papa.unparse({
        fields: progress_data_columns.map(col => col.header), // Extract column headers
        data: rows.map(row => progress_data_columns.map(col => row[col.key])), // Map rows to column keys
      });
      const file_blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const file = new File([file_blob], 'progress_data.csv');
      return file;
    }
    function workflowsData(group_workflow_project) {
      const rows = [];
      const workflows_data = [];
      Object.values(group_workflow_project).forEach((projects_map, workflow_index) => {
        Object.values(projects_map).forEach((fields, project_index) => {
          const fields_asc = sortBy(fields, [o => o.field.toLowerCase()]);
          fields_asc.forEach((field, field_index) => {
            const row = getFieldValues(field);
            row.number = `${workflow_index + 1}.${project_index + 1}.${field_index + 1}`;

            // Validate and format date fields
            const date_fields = ['actual_start', 'actual_finish', 'planned_start', 'planned_finish', 'est_finish_date'];
            date_fields.forEach((date_field) => {
              if (row[date_field] && dayjs(row[date_field]).isValid()) {
                row[date_field] = dayjs(row[date_field]).format('DD-MM-YYYY');
              }
            });
            workflows_data.push(row);
            rows.push(row); // Add row to CSV data
          });
        });
      });
      return { rows, workflows_data };
    }
    function getProgressHistoryFile(progress_data, group_workflow_project) {
      const rows = [];
      const dates = progress_data?.custom_agent_data?.map?.(item => item.actual_date);
      const unique_dates = [...new Set(dates)];
      const { workflows_data } = workflowsData(group_workflow_project);
      unique_dates.forEach((date) => {
        let formatted_date = formattedDate(date);
        // Validate and format date fields
        if (formatted_date && dayjs(formatted_date).isValid()) {
          formatted_date = dayjs(formatted_date).format('DD-MM-YYYY');
        }
        workflows_data.forEach((row) => {
          const row_data = {
            id: row.id,
            workflow: row.workflow,
            activity: row.activity,
            scope: row.scope,
            sublayer: row.sublayer,
            layer: row.layer,
            value: progress_data?.[row.workflow_uid]?.[row.sublayer_uid]?.[row.activity_uid]?.[date] || 0,
            date: formatted_date,
          };
          if (row_data.value)
            rows.push(row_data); // Add row to CSV data
        });
      });

      const csv = Papa.unparse({
        fields: progress_history_columns.map(col => col.header), // Extract column headers
        data: rows.map(row => progress_history_columns.map(col => row[col.key])), // Map rows to column keys
      });
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const file = new File([blob], 'progress_history.csv');
      return file;
    }

    function getClassesBreakdownFile(classes_breakdown) {
      const headers = classes_breakdown_columns.map(col => col.header);

      const rows = (classes_breakdown || []).map((row) => {
        return classes_breakdown_columns.map(({ key }) => row[key] || '');
      });

      const csv = Papa.unparse({
        fields: headers,
        data: rows,
      });

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const file = new File([blob], 'classes.csv');
      return file;
    }

    const updated_report_data = getFilteredData(report_data);
    const { group_workflow_project, progress_data, classes_breakdown } = await getFormattedData(updated_report_data);
    const progress_data_file = getProgressDataFile(group_workflow_project);
    const progress_history_file = getProgressHistoryFile(progress_data, group_workflow_project);
    const classes_breakdown_file = getClassesBreakdownFile(classes_breakdown);
    return [progress_data_file, progress_history_file, classes_breakdown_file];
  }

  return {
    cancelExportingTerraReport,
    exportTerraReports,
    setExporting,
    is_report_exporting,
    getFormattedData,
    getFieldValues,
    formattedDate,
    progressFiles,
  };
}
